/**
 * Nav rail (Phase 1: items #9 theme toggle + #12 tooltip overlap fix).
 *
 * Tooltip migration: custom CSS tooltip (which overlapped per Rick's screenshot)
 * is replaced with Radix Tooltip via the shared <Tooltip /> wrapper. Radix
 * auto-handles viewport-collision (flip/shift) so overlaps cannot happen.
 *
 * Bottom: theme toggle (item #9) + density toggle (item #7) + admin links.
 */
import React from 'react';
import { Link, useLocation } from 'wouter';
import { motion } from 'framer-motion';
import {
  Inbox,
  FolderOpen,
  Globe,
  Hammer,
  Settings,
  SlidersHorizontal,
  Users,
  ChevronLeft,
  ChevronRight,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import { useTenant } from '../../lib/tenant-context.js';
import { useAuth } from '../../hooks/useAuth.js';
import { Tooltip } from '../ui/tooltip.js';
import ThemeToggle from '../ui/theme-toggle.js';
import PrivacyToggle from '../ui/privacy-toggle.js';

export interface NavItem {
  href: string;
  icon: LucideIcon;
  label: string;
  shortcut?: string;
  aliases?: string[];
}

export const NAV_ITEMS: NavItem[] = [
  { href: '/inbox',          icon: Inbox,      label: 'Inbox',          shortcut: 'g i' },
  { href: '/deals',          icon: FolderOpen, label: 'Deals',          shortcut: 'g d' },
  // Bids get their own surface because the Deal Contacts rail deliberately
  // excludes site-prep deals (2026-08-27) - without this entry a submitted
  // marketplace bid is invisible in POE (Rick, 2026-08-28).
  { href: '/bids',           icon: Hammer,     label: 'Bids',           shortcut: 'g b' },
  { href: '/contacts',       icon: Users,      label: 'Contacts',       shortcut: 'g p', aliases: ['/parties'] },
  // Map removed from the nav (Rick, 2026-08-16) - the /map ROUTE stays
  // registered in App.tsx and reachable by URL; only the menu entry goes.
];

export const BOTTOM_ITEMS: NavItem[] = [
  // AI Calls is deliberately NOT in this nav. POE is a conduit; the team works
  // in LA-AI and does not come here (D-53). The reporting dashboard lives in
  // LA-AI and reads POE over the signed cross-app channel. The /admin/ai-calls
  // ROUTE stays registered and reachable by URL for admin debugging, exactly
  // like /map after PR #67 - only the menu entry goes.
  { href: '/admin/autonomy',           icon: SlidersHorizontal, label: 'Autonomy' },
  { href: '/admin/connected-accounts', icon: Globe,    label: 'Integrations' },
  { href: '/admin/settings',           icon: Settings, label: 'Settings' },
];

interface NavRailProps {
  onToggleList?: () => void;
  listCollapsed?: boolean;
  /** Open Neo chat panel. Wired by ThreePaneLayout (lifted state). */
  onToggleNeo?: () => void;
  /** Whether Neo panel is currently open (controls active state). */
  neoOpen?: boolean;
}

export default function NavRail({
  onToggleList,
  listCollapsed,
}: NavRailProps) {
  const [location] = useLocation();
  const { tenant } = useTenant();
  const { logout } = useAuth();

  const isActive = (item: NavItem) => {
    const paths = [item.href, ...(item.aliases ?? [])];
    return paths.some((href) => location === href || location.startsWith(`${href}/`));
  };

  return (
    <nav
      className="flex flex-col items-center gap-1 w-14 h-dvh max-h-dvh min-h-0 flex-shrink-0 overflow-y-auto overflow-x-hidden overscroll-contain bg-surface-raised border-r border-line py-3"
      aria-label="Main navigation"
      role="navigation"
    >
      {/* Logo / Brand */}
      <Tooltip content={tenant?.brandName || 'POE'} side="right">
        <div
          className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center mb-3 flex-shrink-0"
          aria-label={tenant?.brandName || 'POE'}
        >
          <span className="text-primary-fg font-bold text-sm select-none">
            {(tenant?.brandName?.[0] || 'P').toUpperCase()}
          </span>
        </div>
      </Tooltip>

      {/* Main nav items */}
      <div className="flex flex-col items-center gap-1 flex-1">
        {NAV_ITEMS.map(({ href, icon: Icon, label, shortcut, aliases }) => (
          <NavButton
            key={href}
            href={href}
            icon={<Icon size={18} />}
            label={label}
            shortcut={shortcut}
            active={isActive({ href, icon: Icon, label, shortcut, aliases })}
          />
        ))}
      </div>

      {/* Collapse list pane toggle */}
      {onToggleList && (
        <Tooltip
          content={listCollapsed ? 'Show list pane' : 'Hide list pane'}
          shortcut="["
          side="right"
        >
          <button
            onClick={onToggleList}
            className="w-9 h-9 rounded-md flex items-center justify-center text-ink-muted hover:text-ink hover:bg-surface-sunken transition-colors duration-150"
            aria-label={listCollapsed ? 'Show list pane' : 'Hide list pane'}
          >
            {listCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </Tooltip>
      )}

      {/* Theme toggle + admin links */}
      <div className="flex flex-col items-center gap-1 pt-2 border-t border-line w-full px-2 mt-1">
        <ThemeToggle />
        <PrivacyToggle />

        {BOTTOM_ITEMS.map(({ href, icon: Icon, label, aliases }) => (
          <NavButton
            key={href}
            href={href}
            icon={<Icon size={16} />}
            label={label}
            active={isActive({ href, icon: Icon, label, aliases })}
            small
          />
        ))}

        <Tooltip content="Log out" side="right">
          <button
            type="button"
            onClick={() => logout()}
            className="w-9 h-8 rounded-md flex items-center justify-center text-ink-subtle hover:text-danger hover:bg-danger/10 transition-colors duration-150"
            aria-label="Log out"
            data-testid="button-logout"
          >
            <LogOut size={16} />
          </button>
        </Tooltip>
      </div>
    </nav>
  );
}

interface NavButtonProps {
  href: string;
  icon: React.ReactNode;
  label: string;
  shortcut?: string;
  active?: boolean;
  small?: boolean;
}

function NavButton({ href, icon, label, shortcut, active, small }: NavButtonProps) {
  return (
    <Tooltip content={label} shortcut={shortcut} side="right">
      <Link
        href={href}
        aria-current={active ? 'page' : undefined}
        aria-label={label}
      >
        {/* Wouter Link already renders the <a>. This motion.div is the visual wrapper,
            not another anchor, so we avoid the DOM-nesting <a>-inside-<a> bug. */}
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.1 }}
          className={[
            'relative w-9 rounded-md flex items-center justify-center',
            'transition-colors duration-150 cursor-pointer',
            small ? 'h-8' : 'h-9',
            active
              ? 'bg-selected text-selected-fg'
              : 'text-ink-muted hover:text-ink hover:bg-surface-sunken',
          ].join(' ')}
        >
          {icon}

          {/* Active indicator strip */}
          {active && (
            <motion.div
              layoutId="nav-active"
              className="absolute -right-px top-1 bottom-1 w-0.5 bg-primary rounded-l-full"
              transition={{ type: 'spring', stiffness: 500, damping: 40 }}
            />
          )}
        </motion.div>
      </Link>
    </Tooltip>
  );
}
